import { ChevronDown, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';
import {
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SegmentedControl,
  TooltipProvider,
  cn,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { ReportExplorerFilter } from '../../model';
import { reportFieldFilters, reportFilterDefinitions } from '../../reportFilters';
import {
  ReportFieldRailView,
  type ReportDimension,
  type ReportFilterField,
  type ReportMetric,
} from '../../reportEnums';
import type { ReportSelectionSection } from '../ReportSelectionDialog';
import { FieldGroup } from './FieldGroup';
import { matchingFieldSections } from './fieldSearch';

interface ReportFieldRailProps {
  dimensionSections: readonly ReportSelectionSection<ReportDimension>[];
  metricSections: readonly ReportSelectionSection<ReportMetric>[];
  dimensions: readonly ReportDimension[];
  metrics: readonly ReportMetric[];
  filters: readonly ReportExplorerFilter[];
  disabled: boolean;
  filterField: (dimension: ReportDimension) => ReportFilterField | undefined;
  isDimensionDisabled: (candidate: ReportDimension) => boolean;
  isMetricDisabled: (candidate: ReportMetric) => boolean;
  /** Explains a disabled option; only evaluated when its tooltip opens. */
  dimensionDisabledReason: (candidate: ReportDimension) => string;
  metricDisabledReason: (candidate: ReportMetric) => string;
  renderFilter: (field: ReportFilterField) => ReactNode;
  onDimensionsChange: (dimensions: ReportDimension[]) => void;
  onMetricsChange: (metrics: ReportMetric[]) => void;
  onRemoveFilter: (field: ReportFilterField) => void;
}

/**
 * Inline dimension/metric picker. Toggles change the selection; the page runs the table.
 * Below `xl` the picker collapses behind a summary row so the table stays reachable.
 */
export function ReportFieldRail({
  dimensionSections,
  metricSections,
  dimensions,
  metrics,
  filters,
  disabled,
  filterField,
  isDimensionDisabled,
  isMetricDisabled,
  dimensionDisabledReason,
  metricDisabledReason,
  renderFilter,
  onDimensionsChange,
  onMetricsChange,
  onRemoveFilter,
}: ReportFieldRailProps) {
  const [search, setSearch] = useState('');
  const [view, setView] = useState(ReportFieldRailView.All);
  const [expanded, setExpanded] = useState<ReadonlySet<ReportDimension>>(() => new Set());
  const showDimensions = view !== ReportFieldRailView.Metrics;
  const showMetrics = view !== ReportFieldRailView.Dimensions;
  const noMatches =
    search.trim() !== '' &&
    !(showDimensions && matchingFieldSections(dimensionSections, search).length) &&
    !(showMetrics && matchingFieldSections(metricSections, search).length);
  // Only read below xl; the picker is always shown on desktop.
  const [open, setOpen] = useState(false);

  function toggleExpanded(dimension: ReportDimension, open: boolean) {
    setExpanded((current) => {
      const next = new Set(current);
      if (open) next.add(dimension);
      else next.delete(dimension);
      return next;
    });
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Card className="border-border bg-card flex min-h-0 min-w-0 flex-col gap-0 overflow-hidden rounded-md p-0 shadow-[var(--shadow-xs)] xl:sticky xl:top-4 xl:h-[calc(100dvh-2rem)]">
        <Collapsible open={open} onOpenChange={setOpen} className="flex min-h-0 flex-1 flex-col">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="hover:bg-muted/50 active:bg-muted flex min-h-11 w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors xl:hidden"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">
                  {dashboardT('reportExplorer.fields.title')}
                </span>
                <span className="text-muted-foreground block truncate text-xs">
                  {dashboardT('reportExplorer.fields.dimensionCount', {
                    count: dimensions.length,
                  })}
                  {' · '}
                  {dashboardT('reportExplorer.fields.metricCount', { count: metrics.length })}
                  {filters.length
                    ? ` · ${dashboardT('reportExplorer.fields.filterCount', { count: filters.length })}`
                    : ''}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
                  open && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent
            forceMount
            className="border-border flex min-h-0 flex-1 flex-col border-t data-[state=closed]:hidden xl:border-t-0 xl:data-[state=closed]:flex"
          >
            <div className="border-border space-y-3 border-b p-3">
              <DashboardTableSearchInput
                containerClassName="sm:w-full"
                aria-label={dashboardT('reportExplorer.fields.search')}
                placeholder={dashboardT('reportExplorer.fields.search')}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
              <SegmentedControl
                className="w-full [&>*]:flex-1"
                ariaLabel={dashboardT('reportExplorer.fields.view')}
                value={view}
                options={Object.values(ReportFieldRailView).map((value) => ({
                  value,
                  label: dashboardT(`reportExplorer.fields.view.${value}`),
                }))}
                onValueChange={setView}
              />
            </div>
            <div className="max-h-[60dvh] min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-2 py-3 xl:max-h-none">
              {noMatches ? (
                <p className="text-muted-foreground px-2 py-6 text-center text-sm">
                  {dashboardT(`reportExplorer.fields.noMatches.${view}`, { search: search.trim() })}
                </p>
              ) : null}
              {showDimensions ? (
                <FieldGroup
                  title={dashboardT('reportExplorer.dimensions')}
                  sections={dimensionSections}
                  selected={dimensions}
                  search={search}
                  disabled={disabled}
                  isDisabled={isDimensionDisabled}
                  disabledReason={dimensionDisabledReason}
                  onChange={onDimensionsChange}
                  renderAside={(dimension, checked) => {
                    const field = filterField(dimension);
                    if (!field) return null;
                    const open = checked && expanded.has(dimension);
                    return (
                      <>
                        <FilterSummary
                          field={field}
                          filters={filters}
                          removable={!checked}
                          disabled={disabled}
                          onRemove={onRemoveFilter}
                        />
                        {checked ? (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 shrink-0"
                            aria-expanded={open}
                            aria-label={dashboardT('reportExplorer.fields.filter', {
                              field: dashboardT(reportFilterDefinitions[field].labelKey),
                            })}
                            onClick={() => {
                              toggleExpanded(dimension, !open);
                            }}
                          >
                            <ChevronDown
                              className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
                              aria-hidden="true"
                            />
                          </Button>
                        ) : null}
                      </>
                    );
                  }}
                  renderBelow={(dimension, checked) => {
                    const field = filterField(dimension);
                    return field && checked && expanded.has(dimension) ? (
                      <div className="py-1.5 pl-2">{renderFilter(field)}</div>
                    ) : null;
                  }}
                />
              ) : null}
              {showMetrics ? (
                <FieldGroup
                  title={dashboardT('reportExplorer.metrics')}
                  sections={metricSections}
                  selected={metrics}
                  search={search}
                  disabled={disabled}
                  isDisabled={isMetricDisabled}
                  disabledReason={metricDisabledReason}
                  onChange={onMetricsChange}
                />
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </TooltipProvider>
  );
}

function FilterSummary({
  field,
  filters,
  removable,
  disabled,
  onRemove,
}: {
  field: ReportFilterField;
  filters: readonly ReportExplorerFilter[];
  removable: boolean;
  disabled: boolean;
  onRemove: (field: ReportFilterField) => void;
}) {
  const active = reportFieldFilters(filters, field)[0];
  if (!active) return null;
  const fieldLabel = dashboardT(reportFilterDefinitions[field].labelKey);

  return (
    <span className="text-accent-foreground flex shrink-0 items-center gap-1 text-xs font-medium">
      {dashboardT(`reportExplorer.fields.filterSummary.${active.mode}`, {
        count: active.values.length,
      })}
      {removable ? (
        <button
          type="button"
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground cursor-pointer disabled:cursor-not-allowed"
          aria-label={dashboardT('reportExplorer.fields.removeFilter', { field: fieldLabel })}
          onClick={() => {
            onRemove(field);
          }}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}
